/**
 * Single client-side data path for the design-approval trail.
 *
 * Portal splash and staff "Client view" preview both build trail inputs here.
 * Structure groups/labels come from the portal project mirror written by the
 * server (release / sync / backfill) — never from live projectFileStreams /
 * projectFiles, which portal guests cannot read.
 */

import type {
  PortalDeliverableApproval,
  PortalFileRelease,
  PortalProject,
  PortalReleaseBundle,
} from '../types';
import type { BuildApprovalTrailInput, ApprovalTrail } from './portalApprovalTrail';
import { buildApprovalTrail } from './portalApprovalTrail';
import type { ApprovalTrailStructureGroup } from './projectFileStreamGroups';
import type { PortalApprovalTargetGroup } from './portalDeliverableApprovalService';
import type { StructureRef } from './proposalStructureRefs';

export type PortalTrailGroupingSnapshot = {
  structures: StructureRef[];
  structureGroups: ApprovalTrailStructureGroup[];
};

function asStructureRef(raw: unknown): StructureRef | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const id = typeof row.id === 'string' ? row.id.trim() : '';
  const name = typeof row.name === 'string' ? row.name.trim() : '';
  if (!id) return null;
  return { id, name: name || id };
}

function asStructureGroup(raw: unknown): ApprovalTrailStructureGroup | null {
  if (!rowIsObject(raw)) return null;
  const row = raw;
  const label = typeof row.label === 'string' ? row.label.trim() : '';
  const structureIds = Array.isArray(row.structureIds)
    ? row.structureIds.map(id => String(id ?? '').trim()).filter(Boolean)
    : [];
  if (!label || !structureIds.length) return null;
  const structureNames = Array.isArray(row.structureNames)
    ? row.structureNames.map(n => String(n ?? '').trim())
    : [];
  return {
    label,
    structureIds,
    structureNames: structureIds.map((_, i) => structureNames[i] || structureIds[i]!),
  };
}

function rowIsObject(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object';
}

/** Parse the server-written grouping mirror from a portal project row. */
export function portalTrailGroupingFromProject(
  project: PortalProject | null | undefined,
): PortalTrailGroupingSnapshot {
  const structures = (project?.approvalTrailStructures ?? [])
    .map(asStructureRef)
    .filter((s): s is StructureRef => !!s);
  const structureGroups = (project?.approvalTrailStructureGroups ?? [])
    .map(asStructureGroup)
    .filter((g): g is ApprovalTrailStructureGroup => !!g);
  return { structures, structureGroups };
}

/**
 * Soft fallback when the portal mirror has not been synced yet: unique
 * structures named on releases (no custom Files-tab group labels). Same for
 * splash and preview, so empty-mirror portals still match each other.
 */
export function portalTrailGroupingFromReleases(
  releases: PortalFileRelease[],
): PortalTrailGroupingSnapshot {
  const order: string[] = [];
  const names = new Map<string, string>();
  for (const release of releases) {
    const ids = release.structureIds ?? [];
    const releaseNames = release.structureNames ?? [];
    ids.forEach((raw, i) => {
      const id = (raw || '').trim();
      if (!id) return;
      if (!names.has(id)) {
        names.set(id, (releaseNames[i] || '').trim() || id);
        order.push(id);
      }
    });
  }
  return {
    structures: order.map(id => ({ id, name: names.get(id) || id })),
    structureGroups: [],
  };
}

/** Prefer portal mirror; fall back to release tags so both surfaces stay aligned. */
export function resolvePortalTrailGrouping(
  project: PortalProject | null | undefined,
  releases: PortalFileRelease[],
): PortalTrailGroupingSnapshot {
  const fromPortal = portalTrailGroupingFromProject(project);
  if (fromPortal.structures.length > 0 || fromPortal.structureGroups.length > 0) {
    return fromPortal;
  }
  return portalTrailGroupingFromReleases(releases);
}

export type ClientPortalTrailPropsInput = {
  releases: PortalFileRelease[];
  bundles?: PortalReleaseBundle[];
  /** Portal project row for the selected deal (multi-project map entry or primary). */
  portalProject?: PortalProject | null;
  legacyApprovals?: PortalApprovalTargetGroup;
  proposalApprovedAt?: Date | null;
  proposalApprovedByName?: string | null;
  /**
   * Optional legacy approval fields when callers pass the effective portal
   * slice instead of a pre-grouped target map.
   */
  plansApproval?: PortalDeliverableApproval | null;
  plansApprovalHistory?: PortalDeliverableApproval[];
  modelApproval?: PortalDeliverableApproval | null;
  modelApprovalHistory?: PortalDeliverableApproval[];
  conceptApproval?: PortalDeliverableApproval | null;
  conceptApprovalHistory?: PortalDeliverableApproval[];
};

/**
 * Build trail inputs for the public portal AND staff Client view.
 * Intentionally omits hub files/streams so the two surfaces cannot drift.
 */
export function buildClientPortalTrailInput(
  input: ClientPortalTrailPropsInput,
): BuildApprovalTrailInput {
  const grouping = resolvePortalTrailGrouping(input.portalProject, input.releases);
  return {
    releases: input.releases,
    bundles: input.bundles,
    structures: grouping.structures,
    structureGroups: grouping.structureGroups,
    // Never pass hub streams/files — portal guests cannot read them, and staff
    // Client view must mirror that path exactly.
    files: undefined,
    streams: undefined,
    legacyApprovals: input.legacyApprovals,
    proposalApprovedAt: input.proposalApprovedAt,
    proposalApprovedByName: input.proposalApprovedByName,
  };
}

/** Lane / trunk row labels in display order (parity asserts). */
export function approvalTrailRowLabels(trail: ApprovalTrail): string[] {
  const lanes = trail.lanes.length ? trail.lanes : trail.trunk ? [trail.trunk] : [];
  return lanes.map(lane => lane.label);
}

/** Build the trail the portal guest and staff Client view both render. */
export function buildClientPortalApprovalTrail(input: ClientPortalTrailPropsInput): ApprovalTrail {
  return buildApprovalTrail(buildClientPortalTrailInput(input));
}
